// src/Applications.js
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db, auth } from './firebase';
import { FaCheck, FaTimes, FaUndo  } from 'react-icons/fa';
const ApplicationDetailModal = ({ app, onClose, onApprove, onDecline, onMoveToPending, processingId }) => {
  if (!app) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Application Details</h3>
          <button onClick={onClose} className="modal-close-btn">&times;</button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-item"><strong>Name:</strong> {app.firstName} {app.lastName}</div>
            <div className="detail-item"><strong>Email:</strong> {app.email}</div>
            <div className="detail-item"><strong>Phone:</strong> {app.phone}</div>
            <div className="detail-item"><strong>Address:</strong> {app.address}</div>
            <div className="detail-item"><strong>Experience:</strong> {app.drivingExperience}</div>
            <div className="detail-item"><strong>Platforms:</strong> {app.drivingPlatforms?.join(', ')}</div>
            {app.referralCodeUsed && (
      <div className="detail-item"><strong>Referral Code Used:</strong> {app.referralCodeUsed}</div>
    )}
  
          </div>
          
          <h4 className="screenshot-header">Proof Screenshot(s)</h4>
          <div className="screenshot-container">
            {app.proofScreenshotUrl?.map((url, index) => (
              <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={`Proof screenshot ${index + 1}`} className="screenshot-img" />
              </a>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          {app.status === 'pending' && (
            <>
              <button
                onClick={() => onDecline(app.id, app.email, app.firstName)}
                disabled={processingId === app.id}
                className="btn btn-decline"
              >
                <FaTimes />
                {processingId === app.id ? '...' : 'Decline'}
              </button>
              <button
                onClick={() => onApprove(app.id, app.email)}
                disabled={processingId === app.id}
                className="btn btn-approve"
              >
                <FaCheck />
                {processingId === app.id ? '...' : 'Approve'}
              </button>
            </>
          )}

          {app.status === 'approved' && (
            <p className="status-text-approved">This application has been approved.</p>
          )}

          {app.status === 'declined' && (
            <button
              onClick={() => onMoveToPending(app.id)}
              disabled={processingId === app.id}
              className="btn btn-view" // Using 'view' style for this action
            >
              <FaUndo />
              {processingId === app.id ? '...' : 'Move to Pending'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filter, setFilter] = useState('pending'); // ✅ ADD THIS LINE

  useEffect(() => {
  setLoading(true); // Show loading indicator when switching tabs
  const applicationsRef = collection(db, "applications");

  // ✅ The query now uses the 'filter' state variable
  const q = query(applicationsRef, where("status", "==", filter));

  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const appsData = [];
    querySnapshot.forEach((doc) => {
      appsData.push({ id: doc.id, ...doc.data() });
    });
    setApplications(appsData);
    setLoading(false);
  }, (err) => {
    console.error("Error fetching applications:", err);
    setError("Failed to load applications.");
    setLoading(false);
  });

  return () => unsubscribe();
}, [filter]); // ✅ IMPORTANT: Add 'filter' to the dependency array
  const handleApprove = async (applicationId, driverEmail) => {
    if (!window.confirm(`Are you sure you want to APPROVE this driver: ${driverEmail}?`)) return;
    setProcessingId(applicationId);
    setError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("You are not logged in.");
      const idToken = await user.getIdToken(true);
      const result = await fetch("https://us-central1-drivio-dashboard-40e1f.cloudfunctions.net/approveDriver", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ applicationId, email: driverEmail }),
      });
      const responseData = await result.json();
      if (!result.ok) throw new Error(responseData.message || "Approval failed");
      alert(responseData.message || 'Driver approved successfully and an activation email has been sent.');
    } catch (err) {
      console.error("Approval failed:", err);
      alert(`Approval failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (applicationId, driverEmail, firstName) => {
    if (!window.confirm(`Are you sure you want to DECLINE this driver: ${driverEmail}?`)) return;
    setProcessingId(applicationId);
    setError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("You are not logged in.");
      const idToken = await user.getIdToken(true);
      const result = await fetch("https://us-central1-drivio-dashboard-40e1f.cloudfunctions.net/declineDriver", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ applicationId, email: driverEmail, firstName }),
      });
      const responseData = await result.json();
      if (!result.ok) throw new Error(responseData.message || "Decline failed");
      alert(responseData.message || 'Driver declined successfully and an email has been sent.');
    } catch (err) {
      console.error("Decline failed:", err);
      alert(`Decline failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };
  const handleMoveToPending = async (applicationId) => {
    if (!window.confirm(`Are you sure you want to move this application back to Pending?`)) return;
    setProcessingId(applicationId);
    setError('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("You are not logged in.");
      const idToken = await user.getIdToken(true);
      
      const result = await fetch("https://us-central1-drivio-dashboard-40e1f.cloudfunctions.net/reconsiderApplication", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ applicationId }),
      });

      const responseData = await result.json();
      if (!result.ok) throw new Error(responseData.message || "Action failed");
      
      alert(responseData.message || 'Application moved to pending.');
      setSelectedApp(null); // Close the modal on success

    } catch (err) {
      console.error("Move to pending failed:", err);
      alert(`Action failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };


  if (loading) return <p className="loading-text">Loading applications...</p>;
  if (error) return <p className="error-text">{error}</p>;
   const viewConfig = {
    pending: {
      title: 'Pending Driver Applications',
      subtitle: 'Review and process new applications.',
    },
    approved: {
      title: 'Approved Applications',
      subtitle: 'A history of all approved drivers.',
    },
    declined: {
      title: 'Declined Applications',
      subtitle: 'A history of all declined applications.',
    },
  };

  return (
    <div className="card">
         <h2 className="page-header">{viewConfig[filter].title}</h2>
      <p className="page-subheader">{viewConfig[filter].subtitle}</p>
       <div className="filter-tabs">
    <button 
      className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
      onClick={() => setFilter('pending')}
    >
      Pending
    </button>
    <button 
      className={`filter-tab ${filter === 'approved' ? 'active' : ''}`}
      onClick={() => setFilter('approved')}
    >
      Approved
    </button>
    <button 
      className={`filter-tab ${filter === 'declined' ? 'active' : ''}`}
      onClick={() => setFilter('declined')}
    >
      Declined
    </button>
  </div>
       {selectedApp && (
        <ApplicationDetailModal 
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
          onApprove={handleApprove}
          onDecline={handleDecline}
          onMoveToPending={handleMoveToPending}
          processingId={processingId}
        />
      )}

      {applications.length === 0 ? (
        <p>No {filter} applications found.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Submitted At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map(app => (
              <tr key={app.id}>
                <td>{app.firstName} {app.lastName}</td>
                <td>{app.email}</td>
                <td>{app.submittedAt?.toDate().toLocaleString()}</td>
               <td>
  <button
    onClick={() => setSelectedApp(app)}
    className="btn btn-view"
  >
    View Details
  </button>
</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default Applications;
